import React from 'react';
import { Box, Typography, Skeleton, Stack } from '@mui/material';
import { DASH } from './dashboardTokens';

/**
 * Pokojná plocha pre panely prehľadu: vlasová linka, žiadny tieň, veľký padding.
 * Vizuálnu váhu si necháva hero karta, nie zvyšok stránky.
 */
export const Panel = ({ children, sx = {}, padded = true }) => (
    <Box
        sx={{
            borderRadius: `${DASH.radius}px`,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            ...(padded ? { p: DASH.pad } : null),
            ...sx,
        }}
    >
        {children}
    </Box>
);

/**
 * Hlavička panelu — názov v displejovom reze, voliteľný popis, akcie vpravo.
 * Bez ikony: opakovaná ikonka pri každom nadpise bola najhlasnejší šum starého
 * prehľadu a nenesie žiadnu informáciu.
 */
export const PanelHead = ({ title, meta, actions, sx = {} }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2.5,
            ...sx,
        }}
    >
        <Box sx={{ minWidth: 0 }}>
            <Typography
                component="h2"
                sx={{
                    fontFamily: DASH.display,
                    fontSize: '1.0625rem',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                }}
            >
                {title}
            </Typography>
            {meta && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {meta}
                </Typography>
            )}
        </Box>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
    </Box>
);

/**
 * Jedna hodnota v páse metrík — bez rámika, oddelená len vlasovou linkou.
 * Zdieľané prehľadom aj detailom zamestnanca, nech priemery vyzerajú rovnako
 * na oboch miestach.
 */
export const MetricCell = ({ label, value, hint, isFirst }) => (
    <Box
        sx={{
            flex: '1 1 148px',
            minWidth: 0,
            px: { xs: 0, md: 2.5 },
            py: { xs: 1, md: 0 },
            pl: { md: isFirst ? 0 : 2.5 },
            borderLeft: { xs: 'none', md: isFirst ? 'none' : '1px solid' },
            borderColor: { md: 'divider' },
        }}
    >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {label}
        </Typography>
        <Typography
            sx={{
                fontFamily: DASH.display,
                fontSize: '1.5rem',
                fontWeight: 600,
                letterSpacing: '-0.025em',
                lineHeight: 1.25,
                mt: 0.25,
                ...DASH.numeric,
            }}
        >
            {value ?? '–'}
        </Typography>
        {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.75 }} noWrap>
                {hint}
            </Typography>
        )}
    </Box>
);

/** Drobný nadradený štítok — používa sa len tam, kde naozaj zoskupuje obsah. */
export const Eyebrow = ({ children, sx = {} }) => (
    <Typography
        component="span"
        sx={{
            display: 'inline-block',
            fontSize: '0.6875rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            ...sx,
        }}
    >
        {children}
    </Typography>
);

export const PanelBody = ({ children, sx = {} }) => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, ...sx }}>
        {children}
    </Box>
);

/**
 * Prázdny stav je pozvánka konať, nie ospravedlnenie. Preto veta o tom, čo sa tu
 * objaví, a voliteľná akcia — žiadna veľká vyblednutá ikona.
 */
export const EmptyState = ({ title, hint, action, sx = {} }) => (
    <Box
        sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            py: 4,
            px: 0.5,
            ...sx,
        }}
    >
        <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{title}</Typography>
        {hint && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 420 }}>
                {hint}
            </Typography>
        )}
        {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
);

/** Kostra namiesto točiaceho sa kolieska — stránka nepodskočí, keď dáta dorazia. */
export const RowSkeleton = ({ rows = 4 }) => (
    <Stack spacing={2.25} sx={{ py: 0.5 }}>
        {Array.from({ length: rows }).map((_, i) => (
            <Stack key={i} direction="row" spacing={1.75} alignItems="center">
                <Skeleton variant="circular" width={38} height={38} />
                <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width={`${45 + ((i * 13) % 30)}%`} height={16} />
                    <Skeleton variant="text" width={`${28 + ((i * 9) % 22)}%`} height={13} />
                </Box>
                <Skeleton variant="text" width={38} height={16} />
            </Stack>
        ))}
    </Stack>
);

export const BlockSkeleton = ({ height = 200 }) => (
    <Skeleton variant="rounded" height={height} sx={{ borderRadius: `${DASH.radiusSm}px` }} />
);
